import { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Images from "../../utils/Images";
import HeroBanner from "../../components/insurance/HeroBanner";
import SecondaryFilters from "../../components/insurance/SecondaryFilters";
import InsuranceGrid from "../../components/insurance/InsuranceGrid";
import useInsuranceData from "../../components/insurance/useInsuranceData";
import Footer from "../../components/landing/Footer";
import PackageDrawer from "../../components/insurance/PackageDrawer";
import AdvancedSearchDrawer from "../../components/insurance/AdvancedSearchDrawer";
import ComparisonBar from "../../components/insurance/ComparisonBar";
import ComparisonDrawer from "../../components/insurance/ComparisonDrawer";
import policyService from "../../api/PolicyService";
import { toast } from "react-toastify";
import Loader from "../../components/misc/Loader";
import { useNavigate } from "react-router-dom";
import { Plane, ChevronLeft } from "lucide-react";

// Hero slide for Travel Insurance
const travelInsuranceSlide = {
  title: "Travel Insurance",
  subtitle: "Protect your journey, protect your peace of mind",
  description: "Comprehensive travel insurance coverage with flexible packages designed to protect you during your travels.",
  image: Images.road_trip_card,
  gradient: "from-indigo-500 to-purple-600",
};

export default function TravelInsurance() {
  const navigate = useNavigate();
  const [secondaryTab, setSecondaryTab] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const [isPackageOpen, setIsPackageOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const isInitialMount = useRef(true);

  // Comparison feature state
  const [showCompareMode, setShowCompareMode] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const MAX_COMPARISON = 3;

  // Fetch travel insurance policies
  useEffect(() => {
    const fetchPolicies = async () => {
      if (isInitialMount.current) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      try {
        const response = await policyService.getTravelInsurancePolicies();
        if (response.data?.status_code === 200) {
          setPolicies(response.data.data || []);
        } else {
          toast.error(
            response.data?.reason || "An error occurred while fetching travel insurance policies"
          );
          setPolicies([]);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching travel insurance policies"
        );
        setPolicies([]);
      } finally {
        if (isInitialMount.current) {
          setLoading(false);
          isInitialMount.current = false;
        } else {
          setFilterLoading(false);
        }
      }
    };
    fetchPolicies();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Expose package drawer opener for comparison drawer
  useEffect(() => {
    window.openPolicyPackageDrawer = (item) => {
      setSelectedItem(item);
      setIsPackageOpen(true);
      setIsComparisonOpen(false);
    };
    return () => {
      delete window.openPolicyPackageDrawer;
    };
  }, []);

  // Comparison handlers
  const handleToggleCompareMode = () => {
    const newCompareMode = !showCompareMode;
    setShowCompareMode(newCompareMode);
    // Clear selections when exiting compare mode
    if (!newCompareMode) {
      setSelectedPolicies([]);
    }
  };

  const handleToggleSelect = (item) => {
    setSelectedPolicies((prev) => {
      const exists = prev.find((p) => p.id === item.id);
      if (exists) {
        return prev.filter((p) => p.id !== item.id);
      } else {
        if (prev.length >= MAX_COMPARISON) {
          toast.warning(`You can only compare up to ${MAX_COMPARISON} policies at a time`);
          return prev;
        }
        return [...prev, item];
      }
    });
  };

  const handleRemoveFromComparison = (id) => {
    setSelectedPolicies((prev) => prev.filter((p) => p.id !== id));
  };

  const handleClearComparison = () => {
    setSelectedPolicies([]);
  };

  const handleCompare = () => {
    if (selectedPolicies.length >= 2) {
      setIsComparisonOpen(true);
    } else {
      toast.info("Please select at least 2 policies to compare");
    }
  };

  // Reset page when filters change
  const resetPageOnFilterChange = (tab) => {
    if (typeof tab === "string") setSecondaryTab(tab);
    setCurrentPage(1);
  };

  // Use modular data hook with real policy data and advanced filters
  const allItems = useInsuranceData(
    "travel",
    secondaryTab,
    policies,
    advancedFilters
  );

  const ITEMS_PER_PAGE = 8;
  const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allItems.slice(start, start + ITEMS_PER_PAGE);
  }, [allItems, currentPage]);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  if (loading) {
    return <Loader fullScreen={true} text="Loading travel insurance policies..." />;
  }

  return (
    <section className="overflow-hidden relative">
      {/* Hero */}
      <div className="overflow-hidden relative z-0 pt-14 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <HeroBanner slide={travelInsuranceSlide} />
        </motion.div>
      </div>

      {/* Filters */}
      <div className="relative z-10 px-4 py-6 -mt-1 bg-gradient-to-b from-white to-gray-50 sm:py-8 md:py-10 lg:py-12 sm:-mt-2 md:-mt-3 lg:-mt-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-end">
            <div>
              <button
                type="button"
                onClick={() => navigate("/insurance/choose")}
                className="inline-flex items-center gap-1.5 mb-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft size={16} />
                Choose different insurance
              </button>
              <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Select Your Travel Insurance Cover
              </h3>
              <div className="mt-2 w-28 h-1 bg-indigo-500 rounded" />
              <p className="mt-2 text-sm text-gray-600 sm:hidden">
                Choose from our comprehensive travel insurance policies
              </p>
            </div>
            <p className="hidden text-sm text-gray-600 lg:block">
              Choose from our comprehensive travel insurance policies
            </p>
          </div>

          {/* Secondary Filters */}
          <div className="mt-8">
            <SecondaryFilters
              tab={secondaryTab}
              onTabChange={(key) => resetPageOnFilterChange(key)}
              disableOpen={isPackageOpen}
              open={isAdvancedOpen}
              onOpenChange={setIsAdvancedOpen}
              showCompareMode={showCompareMode}
              onToggleCompare={handleToggleCompareMode}
              selectedCount={selectedPolicies.length}
            />

            {/* Advanced Search Drawer */}
            <AdvancedSearchDrawer
              open={isAdvancedOpen}
              onClose={() => setIsAdvancedOpen(false)}
              onApply={(filters) => {
                setAdvancedFilters(filters);
                setCurrentPage(1);
              }}
            />

            {/* Results grid */}
            {loading || filterLoading ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full border-b-2 border-indigo-500 animate-spin"></div>
                    <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-indigo-200 animate-pulse"></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {filterLoading
                      ? "Loading fresh policies..."
                      : "Loading policies..."}
                  </p>
                  {filterLoading && (
                    <p className="text-xs text-gray-500">
                      Finding the best options for you
                    </p>
                  )}
                </div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                    <Plane className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      No travel insurance policies found
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      No travel insurance policies available at the moment. Please check back later.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onClickCapture={(e) => {
                  // Prevent opening drawer when clicking compare checkbox
                  const isCompareBtn = e.target.closest('[data-compare-btn]');
                  if (isCompareBtn) return;

                  // If compare mode is active, toggle selection instead of opening drawer
                  if (showCompareMode) {
                    const card = e.target.closest("[data-card-item]");
                    if (card) {
                      const id = card.getAttribute("data-id");
                      const found = pageItems.find((i) => i.id === id);
                      if (found) {
                        handleToggleSelect(found);
                      }
                    }
                    return;
                  }

                  // Normal behavior: open drawer
                  const card = e.target.closest("[data-card-item]");
                  if (card) {
                    const id = card.getAttribute("data-id");
                    const found = pageItems.find((i) => i.id === id);
                    setSelectedItem(found || null);
                    setIsPackageOpen(true);
                    setIsAdvancedOpen(false);
                  }
                }}
              >
                <InsuranceGrid
                  items={pageItems}
                  currentPage={currentPage}
                  total={allItems.length}
                  perPage={ITEMS_PER_PAGE}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  onGo={(n) => setCurrentPage(n)}
                  showCompareOption={showCompareMode}
                  selectedPolicies={selectedPolicies}
                  onToggleSelect={handleToggleSelect}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <PackageDrawer
        open={isPackageOpen}
        onClose={() => setIsPackageOpen(false)}
        item={selectedItem}
      />

      {/* Comparison Bar */}
      {showCompareMode && selectedPolicies.length >= 1 && (
        <ComparisonBar
          selectedPolicies={selectedPolicies}
          onRemove={handleRemoveFromComparison}
          onCompare={handleCompare}
          onClear={handleClearComparison}
          maxSelection={MAX_COMPARISON}
        />
      )}

      {/* Comparison Drawer */}
      <ComparisonDrawer
        open={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        policies={selectedPolicies}
      />
    </section>
  );
}

